import { useState } from 'react';
import { AlertTriangle, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { DatePicker } from '@/components/ui/DatePicker';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { matrixKey, useGrantSignoff, useStationInstructions } from '@/features/workMode/signoffs';
import { describeWorkError } from '@/features/workMode/api';
import { formatDateOnly, todayIsoDate } from '@/lib/formatters';
import type { FloorWorkstation, WorkstationSignoff, WorkstationSignoffWithCopy } from '@/features/workMode/types';
import type { WorkPerson } from '@/features/workMode/useLookups';

/**
 * Sign a person off at a workstation. Shows which published instruction
 * sets will be copied into their record. Mounted only while open so the
 * preselected station / person come from props.
 */
export function SignoffDialog({
  stations,
  people,
  active,
  initialStationId,
  initialUserId,
  onClose,
  onGranted,
}: {
  stations: FloorWorkstation[];
  people: WorkPerson[];
  active: Map<string, WorkstationSignoff>;
  initialStationId?: string | null;
  initialUserId?: string | null;
  onClose: () => void;
  onGranted?: (signoff: WorkstationSignoffWithCopy) => void;
}) {
  const grant = useGrantSignoff();
  const [stationId, setStationId] = useState(initialStationId ?? '');
  const [userId, setUserId] = useState(initialUserId ?? '');
  const [date, setDate] = useState(todayIsoDate());
  const [notes, setNotes] = useState('');
  const { data: sets = [], isLoading: setsLoading } = useStationInstructions(stationId || null);

  const station = stations.find((s) => s.id === stationId) ?? null;
  const person = people.find((p) => p.id === userId) ?? null;
  const existing = stationId && userId ? active.get(matrixKey(stationId, userId)) : undefined;
  const future = !!date && date > todayIsoDate();

  const submit = () => {
    if (!station || !person) return;
    if (future) {
      toast.error('The sign-off date cannot be in the future.');
      return;
    }
    grant.mutate(
      { workstationId: station.id, userId: person.id, signedOffOn: date || todayIsoDate(), notes },
      {
        onSuccess: (res) => {
          toast.success(`${person.name} is signed off on ${station.name}.`);
          if (res.failedPhotos > 0) {
            toast.warning(
              `${res.failedPhotos} photo${res.failedPhotos === 1 ? '' : 's'} could not be copied. Those steps show the live photo.`,
            );
          }
          onGranted?.(res.signoff);
          onClose();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? 'Sign off again' : 'Sign off'}</DialogTitle>
          <DialogDescription className="sr-only">
            Record that this person is signed off at the workstation, with a copy of its work instructions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Workstation</Label>
              <Select value={stationId} onValueChange={setStationId}>
                <SelectTrigger aria-label="Workstation">
                  <SelectValue placeholder="Choose a station" />
                </SelectTrigger>
                <SelectContent>
                  {stations.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger aria-label="Employee">
                  <SelectValue placeholder="Choose a person" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {stationId && active.has(matrixKey(stationId, p.id)) ? ' · signed off' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-[180px_minmax(0,1fr)]">
            <div className="space-y-1.5">
              <Label htmlFor="signoff-date">Signed off on</Label>
              <DatePicker
                id="signoff-date"
                value={date}
                onChange={setDate}
                placeholder="Date"
                aria-label="Sign-off date"
                showShortcuts={false}
                className={future ? 'border-destructive' : undefined}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signoff-notes">Notes</Label>
              <Textarea
                id="signoff-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Trained by, observed builds, anything to remember"
              />
            </div>
          </div>

          <div className="rounded-xl border border-border">
            <p className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5" />
              Work instructions copied to the record
            </p>
            {!stationId ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">Pick a workstation.</p>
            ) : setsLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : sets.length === 0 ? (
              <p className="flex items-start gap-2 px-3 py-3 text-sm text-fresh-salmon-dark">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                No published work instructions apply at this station.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {sets.map((s) => (
                  <li key={s.instruction_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-foreground">{s.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {s.item_name}
                        {s.item_sku ? ` · ${s.item_sku}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {existing && (
            <p className="text-xs text-muted-foreground">
              Replaces the sign-off from {formatDateOnly(existing.signed_off_on)}. The earlier copy stays in the history.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!station || !person || future || grant.isPending}>
            {grant.isPending ? 'Signing off…' : 'Sign off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
